import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useApiQuery } from '../hooks/useApi';
import { Card, Button } from '../components/ui';
import { ApiClient } from '../services/apiClient';
import type { EstadisticasDashboard } from '../types';

export const DashboardMovilView: React.FC = () => {
  const { t } = useTranslation();
  const { usuario } = useAuth();
  const navigate = useNavigate();

  const {
    data: stats,
    isLoading,
    error
  } = useApiQuery<EstadisticasDashboard>(
    ['dashboard', 'estadisticas'],
    async (): Promise<EstadisticasDashboard> => {
      const response = await ApiClient.get('/dashboard/estadisticas');
      return response as EstadisticasDashboard;
    },
    {
      enabled: true,
    }
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-600 mb-4">{t('errors.load_error')}</p>
          <Button onClick={() => window.location.reload()}>{t('common.retry', 'Reintentar')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4 sticky top-0 z-10">
        <h1 className="text-xl font-bold text-gray-900">{t('nav.dashboard')}</h1>
        <p className="text-sm text-gray-600">
          {usuario?.nombre_completo || usuario?.username}
        </p>
      </div>

      {/* Contenido */}
      <div className="p-4 space-y-4">
        {/* Tarjeta de Reportes */}
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium opacity-90">{t('analysis.total_reports')}</span>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-4xl font-bold mb-1">
              {stats?.total_reportes || 0}
            </p>
            <p className="text-xs opacity-75">
              {stats?.reportes_pendientes || 0} {t('reports.status_pending')}
            </p>
          </div>
        </Card>

        {/* Grid de estadísticas */}
        <div className="grid grid-cols-2 gap-3">
          {/* Máquinas */}
          <Card className="bg-white">
            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-600">{t('analysis.machines')}</span>
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3.375 4.5C2.339 4.5 1.5 5.34 1.5 6.375V13.5h12V6.375c0-1.036-.84-1.875-1.875-1.875h-8.25Z" />
                  <path d="M13.5 15h-12v2.625c0 1.035.84 1.875 1.875 1.875h.375a3 3 0 1 1 6 0h3a.75.75 0 0 0 .75-.75V15Z" />
                </svg>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.maquinas_operativas || 0}
              </p>
              <p className="text-xs text-gray-500">{t('admin.status_active')}</p>
            </div>
          </Card>

          {/* Horas */}
          <Card className="bg-white">
            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-600">{t('common.hours')}</span>
                <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.horas_trabajadas_mes?.toFixed(1) || 0.0}
              </p>
              <p className="text-xs text-gray-500">{t('management.calendar')}</p>
            </div>
          </Card>

          {/* Área */}
          <Card className="bg-white">
            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-600">{t('common.area')}</span>
                <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.volumen_mes_procesado?.toFixed(1) || 0.0}
              </p>
              <p className="text-xs text-gray-500">{t('analysis.total_area')}</p>
            </div>
          </Card>

          {/* Distancia */}
          <Card className="bg-white">
            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-600">{t('common.description', 'Distancia')}</span>
                <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.longitud_mes_metros ? (stats.longitud_mes_metros / 1000).toFixed(1) : 0.0}
              </p>
              <p className="text-xs text-gray-500">km</p>
            </div>
          </Card>
        </div>

        {/* Acciones rápidas */}
        <Card className="bg-white">
          <div className="p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3">
              {t('common.actions')}
            </h3>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/reportes/crear')}
                className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-medium flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {t('reports.new_report')}
              </button>

              <button
                onClick={() => navigate('/reportes')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t('reports.title')}
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DashboardMovilView;
